<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200">
      <base-content class="tw-pt-3 tw-pb-3.5">
        <base-breadcrumbs :breadcrumbs="breadcrumbs" />
      </base-content>
      <base-content class="tw-pt-0 tw-flex tw-items-center">
        <h1 class="tw-text-xl sm:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin">Redeem Credits Settings</h1>
      </base-content>
    </div>
    <base-content>
      <base-list-group>
        <base-list-item>
          <div class="tw-flex tw-justify-between">
            <div class="tw-flex tw-flex-col">
              <span class="tw-font-normal">Require Member Confirmation?</span>
              <span class="tw-text-sm">Shows a member confirmation screen before redeeming credits</span>
              <span class="tw-text-gray-500 tw-text-xs tw-mt-5">* Requires a flip-screen device or another way the member can interact with you.</span>
            </div>
            <base-toggle v-model="memberConfirmSetting.boolValue" @input="saveMemberConfirm"></base-toggle>
          </div>
        </base-list-item>
        <base-list-item :class="!memberConfirmSetting.boolValue ? 'tw-bg-gray-100' : ''">
          <div class="tw-flex tw-justify-between">
            <div class="tw-flex tw-flex-col">
              <div class="tw-font-normal">Enable Tips?</div>
              <span class="tw-text-sm">Allows a member to tip the associate when confirming</span>
              <span class="tw-text-gray-500 tw-text-xs tw-mt-5">* Requires a flip-screen device or another way the member can interact with you.</span>
              <span class="tw-text-gray-500 tw-text-xs">** Requires Member Confirmation setting to be On.</span>
            </div>
            <base-toggle v-model="memberTipSetting.boolValue" @input="saveMemberTip" :disabled="!memberConfirmSetting.boolValue"></base-toggle>
          </div>
        </base-list-item>
        <base-list-item :class="!memberTipSetting.boolValue ? 'tw-bg-gray-100' : ''">
          <div class="tw-flex tw-flex-col">
            <span class="tw-font-normal">Suggested Tip Amounts</span>
            <span class="tw-text-sm">Provide suggested tip amounts for members to select from when redeeming credits</span>
            <base-form ref="tipForm" v-model="isTipFormValid">
              <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4 tw-mt-3">
                <base-text-field label="Tip Amount 1" v-model="suggestedTip1.numValue" type="number" min="0" max="100" step="1" :disabled="!memberTipSetting.boolValue">
                  <template #postfix
                    >%</template
                  >
                </base-text-field>
                <base-text-field label="Tip Amount 2" v-model="suggestedTip2.numValue" type="number" min="0" max="100" step="1" :disabled="!memberTipSetting.boolValue">
                  <template #postfix
                    >%</template
                  >
                </base-text-field>
                <base-text-field label="Tip Amount 3" v-model="suggestedTip3.numValue" type="number" min="0" max="100" step="1" :disabled="!memberTipSetting.boolValue">
                  <template #postfix
                    >%</template
                  >
                </base-text-field>
                <base-text-field label="Tip Amount 4" v-model="suggestedTip4.numValue" type="number" min="0" max="100" step="1" :disabled="!memberTipSetting.boolValue">
                  <template #postfix
                    >%</template
                  >
                </base-text-field>
              </div>
            </base-form>
            <div class="tw-mt-4 tw-flex tw-justify-between tw-items-end">
              <span class="tw-text-gray-500 tw-text-xs">* By default, "No Tip" and "Custom Amount" will also be enabled.</span>
              <base-button type="primary" class="tw-whitespace-nowrap" @click="saveTipAmounts" :disabled="!memberTipSetting.boolValue">Save Tips</base-button>
            </div>
          </div>
        </base-list-item>
        <base-list-item>
          <div class="tw-flex tw-flex-col">
            <span class="tw-font-normal">Insufficient Credits Settings</span>
            <span class="tw-text-sm">How should we handle when a user doesn't have enough credits?</span>
            <base-select
              class="tw-mt-3 md:tw-w-1/2"
              :options="insufficientCreditsOptions"
              label="Insufficient Credits"
              valueKey="value"
              labelKey="label"
              v-model="insufficientCreditsSetting.stringValue"
            ></base-select>
            <div v-if="insufficientCreditsSetting.stringValue === 'RECHARGE_AND_REDEEM'" class="tw-mt-3">
              <span class="tw-text-sm">When recharging, what's the minimum number of credits to redeem?</span>
              <base-text-field class="md:tw-w-1/2" label="Minimum Recharge Credits" v-model="minimumCreditRechargeSetting.numValue" type="number" min="0" />
            </div>
            <div class="tw-mt-4 tw-flex tw-justify-between tw-items-end">
              <span class="tw-text-gray-500 tw-text-xs">Caution: providing services before credits are paid for is done at your own risk if payment fails.</span>
              <base-button type="primary" class="tw-whitespace-nowrap" @click="saveInsufficientCredits" :disabled="!minimumCreditRechargeSetting.numValue < 0">
                Save Insufficient Credits
              </base-button>
            </div>
          </div>
        </base-list-item>
        <base-list-item>
          <div class="tw-flex tw-justify-between">
            <div class="tw-flex tw-flex-col">
              <span class="tw-font-normal">Allow Credits to be Redeemed when Paused?</span>
              <span class="tw-text-sm">Allows a member to redeem a credit when their plan is paused.</span>
            </div>
            <base-toggle v-model="redeemOnPausedPlanSetting.boolValue" @input="saveRedeemOnPausedPlan"></base-toggle>
          </div>
        </base-list-item>
        <base-list-item>
          <div class="tw-flex tw-flex-col">
            <span class="tw-font-normal">Max credits to redeem at once</span>
            <span class="tw-text-sm">If you want to limit all members from redeeming more than a certain amount of credits at once.</span>
            <base-form class="tw-mt-3" ref="maxRedeemForm" v-model="isMaxRedeemFormValid">
              <base-text-field class="md:tw-w-1/2" label="Max Credits" v-model="maxCreditsSetting.numValue" type="number" min="1" max="1000" step="1" />
            </base-form>
            <div class="tw-flex tw-justify-between tw-mt-4 tw-items-end">
              <span class="tw-text-gray-500 tw-text-xs md:tw-w-3/4 tw-mr-3"
                >Caution: This setting is used to protect your business from a member who may present a large order that you are unable to fulfill. Keep in mind that setting this
                too low may create a customer issue if they desire to redeem more of their paid credits.</span
              >
              <base-button type="primary" class="tw-whitespace-nowrap" @click="saveMaxCredits">Save Max Credits</base-button>
            </div>
          </div>
        </base-list-item>
        <base-list-item>
          <div class="tw-flex tw-justify-between">
            <div class="tw-flex tw-flex-col">
              <span class="tw-font-normal">Collect Member Mailing Address?</span>
              <span class="tw-text-sm">Only turn this on if you need to ship goods to your member.</span>
            </div>
            <base-toggle v-model="collectMemberMailingAddressSetting.boolValue" @input="saveMemberMailingAddressSetting"></base-toggle>
          </div>
        </base-list-item>
        <base-list-item :showBottomBorder="false">
          <div class="tw-flex tw-justify-between">
            <div class="tw-flex tw-flex-col">
              <span class="tw-font-normal">Collect Member Date of Birth?</span>
              <span class="tw-text-sm">If you need to collect your member's data of birth for health or outreach purposes, turn this on.</span>
            </div>
            <base-toggle v-model="collectMemberDobSetting.boolValue" @input="saveMemberDobSetting"></base-toggle>
          </div>
        </base-list-item>
      </base-list-group>
    </base-content>
  </div>
</template>
<script>
import EventBus from "@/utils/global-events.js";

export default {
  name: "BusinessRedeemSettings",
  components: {},
  props: {
    businessId: String,
  },
  data() {
    return {
      isLoading: false,
      isTipFormValid: true,
      isMaxRedeemFormValid: true,
      settings: [],
      insufficientCreditsOptions: [
        { label: "Don't allow redemption", value: "DONT_ALLOW_REDEMPTION" },
        { label: "Allow negative credit balance", value: "ALLOW_NEGATIVE_CREDITS" },
        { label: "Recharge and redeem", value: "RECHARGE_AND_REDEEM" },
      ],
    };
  },
  computed: {
    locale() {
      return window.navigator.language;
    },
    breadcrumbs() {
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      return [{ title: "Settings", onClick: () => this.$router.push(`/businesses/${this.businessId}/business`) }, { title: "Redeem Credits Settings" }];
    },
    memberConfirmSetting: {
      get: function() {
        return this.settings.length > 0
          ? this.settings.find((setting) => setting.settingConstant === "MEMBER_REDEEM_CONFIRM")
          : { businessSettingId: null, businessId: this.businessId, settingConstant: "MEMBER_REDEEM_CONFIRM", boolValue: false, stringValue: null, numValue: null };
      },
      set: function(newValue) {
        const index = this.settings.IndexOf((setting) => setting.settingConstant === "MEMBER_REDEEM_CONFIRM");
        this.settings[index].boolValue = newValue;
        this.updateSetting(this.settings[index]);
      },
    },
    memberTipSetting: {
      get() {
        return this.settings.length > 0
          ? this.settings.find((setting) => setting.settingConstant === "MEMBER_REDEEM_TIP")
          : { businessSettingId: null, businessId: this.businessId, settingConstant: "MEMBER_REDEEM_TIP", boolValue: false, stringValue: null, numValue: null };
      },
      set(newValue) {
        const index = this.settings.IndexOf((setting) => setting.settingConstant === "MEMBER_REDEEM_TIP");
        this.settings[index].boolValue = newValue;
      },
    },
    insufficientCreditsSetting: {
      get() {
        return this.settings.length > 0
          ? this.settings.find((setting) => setting.settingConstant === "INSUFFICIENT_CREDITS")
          : {
              businessSettingId: null,
              businessId: this.businessId,
              settingConstant: "INSUFFICIENT_CREDITS",
              boolValue: false,
              stringValue: "DONT_ALLOW_REDEMPTION",
              numValue: null,
            };
      },
      set(newValue) {
        const index = this.settings.IndexOf((setting) => setting.settingConstant === "INSUFFICIENT_CREDITS");
        this.settings[index].boolValue = newValue;
      },
    },
    minimumCreditRechargeSetting: {
      get() {
        return this.settings.length > 0
          ? this.settings.find((setting) => setting.settingConstant === "MINIMUM_CREDIT_RECHARGE")
          : { businessSettingId: null, businessId: this.businessId, settingConstant: "MINIMUM_CREDIT_RECHARGE", boolValue: null, stringValue: null, numValue: 10 };
      },
      set(newValue) {
        const index = this.settings.IndexOf((setting) => setting.settingConstant === "MINIMUM_CREDIT_RECHARGE");
        this.settings[index].boolValue = newValue;
      },
    },
    redeemOnPausedPlanSetting: {
      get() {
        return this.settings.length > 0
          ? this.settings.find((setting) => setting.settingConstant === "ALLOW_REDEEM_ON_PAUSED_PLAN")
          : { businessSettingId: null, businessId: this.businessId, settingConstant: "ALLOW_REDEEM_ON_PAUSED_PLAN", boolValue: false, stringValue: null, numValue: null };
      },
      set(newValue) {
        const index = this.settings.IndexOf((setting) => setting.settingConstant === "ALLOW_REDEEM_ON_PAUSED_PLAN");
        this.settings[index].boolValue = newValue;
      },
    },
    maxCreditsSetting: {
      get() {
        return this.settings.length > 0
          ? this.settings.find((setting) => setting.settingConstant === "MAX_REDEEMABLE_CREDITS")
          : { businessSettingId: null, businessId: this.businessId, settingConstant: "MAX_REDEEMABLE_CREDITS", boolValue: false, stringValue: null, numValue: 10 };
      },
      set(newValue) {
        const index = this.settings.IndexOf((setting) => setting.settingConstant === "MAX_REDEEMABLE_CREDITS");
        this.settings[index].numValue = newValue;
      },
    },
    suggestedTip1: {
      get() {
        return this.settings.length > 0
          ? this.settings.find((setting) => setting.settingConstant === "TIP_LEVEL_CENTS_1")
          : { businessSettingId: null, businessId: this.businessId, settingConstant: "TIP_LEVEL_CENTS_1", boolValue: false, stringValue: null, numValue: null };
      },
      set(newValue) {
        const index = this.settings.IndexOf((setting) => setting.settingConstant === "TIP_LEVEL_CENTS_1");
        this.settings[index].boolValue = newValue;
      },
    },
    suggestedTip2: {
      get() {
        return this.settings.length > 0
          ? this.settings.find((setting) => setting.settingConstant === "TIP_LEVEL_CENTS_2")
          : { businessSettingId: null, businessId: this.businessId, settingConstant: "TIP_LEVEL_CENTS_2", boolValue: false, stringValue: null, numValue: null };
      },
      set(newValue) {
        const index = this.settings.IndexOf((setting) => setting.settingConstant === "TIP_LEVEL_CENTS_2");
        this.settings[index].boolValue = newValue;
      },
    },
    suggestedTip3: {
      get() {
        return this.settings.length > 0
          ? this.settings.find((setting) => setting.settingConstant === "TIP_LEVEL_CENTS_3")
          : { businessSettingId: null, businessId: this.businessId, settingConstant: "TIP_LEVEL_CENTS_3", boolValue: false, stringValue: null, numValue: null };
      },
      set(newValue) {
        const index = this.settings.IndexOf((setting) => setting.settingConstant === "TIP_LEVEL_CENTS_3");
        this.settings[index].boolValue = newValue;
      },
    },
    suggestedTip4: {
      get() {
        return this.settings.length > 0
          ? this.settings.find((setting) => setting.settingConstant === "TIP_LEVEL_CENTS_4")
          : { businessSettingId: null, businessId: this.businessId, settingConstant: "TIP_LEVEL_CENTS_4", boolValue: false, stringValue: null, numValue: null };
      },
      set(newValue) {
        const index = this.settings.IndexOf((setting) => setting.settingConstant === "TIP_LEVEL_CENTS_4");
        this.settings[index].boolValue = newValue;
      },
    },
    collectMemberMailingAddressSetting: {
      get() {
        return this.settings.length > 0
          ? this.settings.find((setting) => setting.settingConstant === "COLLECT_MEMBER_MAILING_ADDRESS")
          : { businessSettingId: null, businessId: this.businessId, settingConstant: "COLLECT_MEMBER_MAILING_ADDRESS", boolValue: false, stringValue: null, numValue: null };
      },
      set(newValue) {
        const index = this.settings.IndexOf((setting) => setting.settingConstant === "COLLECT_MEMBER_MAILING_ADDRESS");
        this.settings[index].boolValue = newValue;
      },
    },
    collectMemberDobSetting: {
      get() {
        return this.settings.length > 0
          ? this.settings.find((setting) => setting.settingConstant === "COLLECT_MEMBER_DOB")
          : { businessSettingId: null, businessId: this.businessId, settingConstant: "COLLECT_MEMBER_DOB", boolValue: false, stringValue: null, numValue: null };
      },
      set(newValue) {
        const index = this.settings.IndexOf((setting) => setting.settingConstant === "COLLECT_MEMBER_DOB");
        this.settings[index].boolValue = newValue;
      },
    },
  },
  mounted() {
    this.loadSettings();
  },

  methods: {
    loadSettings() {
      this.isLoading = true;

      this.$axios
        .get(`/businesses/${this.businessId}/settings`)
        .then((res) => {
          this.settings = res.data;
          this.isLoading = false;
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
    saveMemberConfirm() {
      this.isLoading = true;

      this.$axios
        .put(`/businesses/${this.businessId}/settings/${this.memberConfirmSetting.businessSettingId}`, this.memberConfirmSetting)
        .then(() => {
          this.isLoading = false;
          EventBus.$emit("GLOBAL_NOTIFY", { title: "Setting Saved", message: "Setting was saved successfully" });
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
    saveMemberTip() {
      this.isLoading = true;

      this.$axios
        .put(`/businesses/${this.businessId}/settings/${this.memberTipSetting.businessSettingId}`, this.memberTipSetting)
        .then(() => {
          this.isLoading = false;
          EventBus.$emit("GLOBAL_NOTIFY", { title: "Setting Saved", message: "Setting was saved successfully" });
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
    saveNegativeCredit() {
      this.isLoading = true;

      this.$axios
        .put(`/businesses/${this.businessId}/settings/${this.negativeCreditSetting.businessSettingId}`, this.negativeCreditSetting)
        .then(() => {
          this.isLoading = false;
          EventBus.$emit("GLOBAL_NOTIFY", { title: "Setting Saved", message: "Setting was saved successfully" });
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
    saveRedeemOnPausedPlan() {
      this.isLoading = true;

      this.$axios
        .put(`/businesses/${this.businessId}/settings/${this.redeemOnPausedPlanSetting.businessSettingId}`, this.redeemOnPausedPlanSetting)
        .then(() => {
          this.isLoading = false;
          EventBus.$emit("GLOBAL_NOTIFY", { title: "Setting Saved", message: "Setting was saved successfully" });
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
    saveMaxCredits() {
      this.isLoading = true;

      this.$axios
        .put(`/businesses/${this.businessId}/settings/${this.maxCreditsSetting.businessSettingId}`, this.maxCreditsSetting)
        .then(() => {
          this.isLoading = false;
          EventBus.$emit("GLOBAL_NOTIFY", { title: "Setting Saved", message: "Setting was saved successfully" });
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
    saveTipAmounts() {
      this.isLoading = true;
      const tipSettings = this.settings.filter((setting) => setting.settingConstant.includes("TIP_LEVEL_CENTS"));

      this.$axios
        .put(`/businesses/${this.businessId}/settings`, tipSettings)
        .then(() => {
          this.isLoading = false;
          EventBus.$emit("GLOBAL_NOTIFY", { title: "Settings Saved", message: "Suggested Tip amounts were saved successfully" });
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
    async saveInsufficientCredits() {
      try {
        this.isLoading = true;
        await this.$axios.put(`/businesses/${this.businessId}/settings`, [this.insufficientCreditsSetting, this.minimumCreditRechargeSetting]);
        EventBus.$emit("GLOBAL_NOTIFY", { title: "Settings Saved", message: "Insufficient credits settings were saved successfully" });
      } finally {
        this.isLoading = false;
      }
    },
    saveMemberMailingAddressSetting() {
      this.isLoading = true;

      this.$axios
        .put(`/businesses/${this.businessId}/settings/${this.collectMemberMailingAddressSetting.businessSettingId}`, this.collectMemberMailingAddressSetting)
        .then(() => {
          this.isLoading = false;
          EventBus.$emit("GLOBAL_NOTIFY", { title: "Setting Saved", message: "Setting was saved successfully" });
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
    saveMemberDobSetting() {
      this.isLoading = true;

      this.$axios
        .put(`/businesses/${this.businessId}/settings/${this.collectMemberDobSetting.businessSettingId}`, this.collectMemberDobSetting)
        .then(() => {
          this.isLoading = false;
          EventBus.$emit("GLOBAL_NOTIFY", { title: "Setting Saved", message: "Setting was saved successfully" });
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
    onlyNumber($event) {
      let keyCode = $event.keyCode ? $event.keyCode : $event.which;
      if ((keyCode < 48 || keyCode > 57) && keyCode === 46) {
        // 46 is dot
        $event.preventDefault();
      }
    },
  },
};
</script>
